import React from 'react';
import { Trophy } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface BuyBoxVariant {
  id_catalogo: string;
  title: string;
  Seller: string;
  sale_price: number;
  original_price?: number;
  discounted_price?: number;
  buybox_winner: number | string | boolean;
  imagen?: string;
  url?: string;
  item_id?: number;
  installements?: string | number;
  installements_value?: string | number;
  [key: string]: any;
}

interface BuyBoxVariantsProps {
  variants: BuyBoxVariant[];
  isLoading?: boolean;
  precioReferencia?: number;
  columns?: any[];
  parentRow?: any;
  parentProductId?: string;
}

const BuyBoxVariants: React.FC<BuyBoxVariantsProps> = ({ 
  variants, 
  isLoading = false,
  precioReferencia,
  columns = [],
  parentRow,
  parentProductId
}) => {
  const { t } = useTranslation();
  const [highlightedPrices, setHighlightedPrices] = React.useState<Set<number>>(new Set());
  const [highlightType, setHighlightType] = React.useState<'min' | 'max' | 'range' | null>(null);
  const [activeRangeIndex, setActiveRangeIndex] = React.useState<number | null>(null);
  
  // NO filtrar el producto padre - mostrar todas las variantes
  // ya que queremos ver todos los vendedores del mismo producto
  const filteredVariants = variants || [];

  // Ordenar variantes: winner primero, luego por precio
  const sortedVariants = [...filteredVariants].sort((a, b) => {
    const isWinnerA = a.buybox_winner === 1 || a.buybox_winner === '1' || a.buybox_winner === true;
    const isWinnerB = b.buybox_winner === 1 || b.buybox_winner === '1' || b.buybox_winner === true;
    
    // Winner siempre primero
    if (isWinnerA && !isWinnerB) return -1;
    if (!isWinnerA && isWinnerB) return 1;
    
    // Si ambos son winners o no winners, ordenar por precio
    const priceA = typeof a.sale_price === 'number' ? a.sale_price : (parseFloat(a.sale_price) || 0);
    const priceB = typeof b.sale_price === 'number' ? b.sale_price : (parseFloat(b.sale_price) || 0);
    return priceA - priceB;
  });

  // Calcular estadísticas de precios
  const priceStats = React.useMemo(() => {
    const prices = sortedVariants.map(v => {
      const price = typeof v.sale_price === 'number' ? v.sale_price : (parseFloat(v.sale_price) || 0);
      return price;
    }).filter(p => p > 0);
    if (prices.length === 0) return null;

    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const median = prices[Math.floor(prices.length / 2)];
    
    // Crear rangos de precios para el gráfico
    const range = max - min;
    const bucketSize = range > 0 ? range / 5 : 1; // 5 buckets
    const buckets = Array(5).fill(0);
    
    prices.forEach(price => {
      const bucketIndex = Math.min(Math.floor((price - min) / bucketSize), 4);
      buckets[bucketIndex]++;
    });

    const maxCount = Math.max(...buckets);

    return {
      min,
      max,
      median,
      buckets,
      maxCount,
      bucketSize,
      // El winner es el primer elemento después del sort (que ya ordena winner primero)
      winnerPrice: typeof sortedVariants[0]?.sale_price === 'number' 
        ? sortedVariants[0].sale_price 
        : parseFloat(sortedVariants[0]?.sale_price) || 0
    };
  }, [sortedVariants]);

  const formatPrice = (price: any) => {
    const numPrice = typeof price === 'number' ? price : (parseFloat(price) || 0);
    return `$${numPrice.toLocaleString('es-AR', { 
      minimumFractionDigits: 0, 
      maximumFractionDigits: 0 
    })}`;
  };

  
  // Debug: Ver estructura del primer variant
  if (variants && variants.length > 0) {
  }

  // Always show something when component is rendered
  if (isLoading) {
    return (
      <tr className="bg-gray-50">
        <td colSpan={columns.length || 10} className="px-6 py-4">
          <div className="flex items-center justify-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-amber-600"></div>
            <span className="text-sm text-gray-600">{t('buybox.loading_variants', 'Cargando variantes...')}</span>
          </div>
        </td>
      </tr>
    );
  }

  if (!variants || variants.length === 0) {
    // Don't show message for single products without variants
    return null;
  }

  // Si después de filtrar no quedan variantes, no mostrar nada
  if (filteredVariants.length === 0) {
    return null;
  }

  return (
    <>
      {/* Fila expandida con tabla de variantes */}
      <tr className="bg-gray-50">
        <td colSpan={columns.length || 10} className="p-0">
          <div className="p-4 border-t-2 border-amber-200">

            {/* Gráfico de rangos de precios */}
            {priceStats && (
              <div className="mb-4 bg-white rounded-lg p-3 pt-4 border border-gray-200 max-w-4xl overflow-hidden">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center space-x-4 text-xs">
                    <button 
                      className={`cursor-pointer px-2 py-1 rounded transition-colors ${
                        highlightType === 'min' ? 'bg-green-100' : 'hover:bg-green-50'
                      } focus:outline-none`}
                      onClick={() => {
                        if (highlightType === 'min') {
                          setHighlightedPrices(new Set());
                          setHighlightType(null);
                        } else {
                          setHighlightedPrices(new Set([priceStats.min]));
                          setHighlightType('min');
                          setActiveRangeIndex(null);
                        }
                      }}
                    >
                      <span className="text-gray-500">{t('buybox.min_price', 'Min')}:</span>
                      <span className="ml-1 font-medium text-gray-900">{formatPrice(priceStats.min)}</span>
                    </button>
                    <div>
                      <span className="text-gray-500">{t('buybox.med_price', 'Med')}:</span>
                      <span className="ml-1 font-medium text-gray-900">{formatPrice(priceStats.median)}</span>
                    </div>
                    <button 
                      className={`cursor-pointer px-2 py-1 rounded transition-colors ${
                        highlightType === 'max' ? 'bg-green-100' : 'hover:bg-green-50'
                      } focus:outline-none`}
                      onClick={() => {
                        if (highlightType === 'max') {
                          setHighlightedPrices(new Set());
                          setHighlightType(null);
                        } else {
                          setHighlightedPrices(new Set([priceStats.max]));
                          setHighlightType('max');
                          setActiveRangeIndex(null);
                        }
                      }}
                    >
                      <span className="text-gray-500">{t('buybox.max_price', 'Max')}:</span>
                      <span className="ml-1 font-medium text-gray-900">{formatPrice(priceStats.max)}</span>
                    </button>
                  </div>
                  <div className="flex space-x-1 items-end h-10 w-40">
                    {priceStats.buckets.map((count, index) => {
                      const height = priceStats.maxCount > 0 ? (count / priceStats.maxCount) * 100 : 0;
                      // Encontrar en qué bucket está el winner
                      const winnerPrice = typeof priceStats.winnerPrice === 'number' ? priceStats.winnerPrice : parseFloat(priceStats.winnerPrice);
                      const rangeMin = priceStats.min + index * priceStats.bucketSize;
                      const rangeMax = priceStats.min + (index + 1) * priceStats.bucketSize;
                      const isWinnerInBucket = index === priceStats.buckets.length - 1 
                        ? winnerPrice >= rangeMin && winnerPrice <= rangeMax
                        : winnerPrice >= rangeMin && winnerPrice < rangeMax;
                      const isActive = activeRangeIndex === index;
                      return (
                        <div
                          key={index}
                          className="relative group flex-1 h-full cursor-pointer focus:outline-none"
                          onClick={() => {
                            if (isActive) {
                              // Desactivar si ya está activo
                              setActiveRangeIndex(null);
                              setHighlightedPrices(new Set());
                              setHighlightType(null);
                            } else {
                              // Activar este rango
                              const rangeMin = priceStats.min + index * priceStats.bucketSize;
                              const rangeMax = priceStats.min + (index + 1) * priceStats.bucketSize;
                              const pricesInRange = new Set(
                                sortedVariants
                                  .filter(v => {
                                    const price = typeof v.sale_price === 'number' ? v.sale_price : parseFloat(v.sale_price);
                                    // Para el último bucket, incluir el valor máximo
                                    if (index === priceStats.buckets.length - 1) {
                                      return price >= rangeMin && price <= rangeMax;
                                    }
                                    return price >= rangeMin && price < rangeMax;
                                  })
                                  .map(v => typeof v.sale_price === 'number' ? v.sale_price : parseFloat(v.sale_price))
                              );
                              setActiveRangeIndex(index);
                              setHighlightedPrices(pricesInRange);
                              setHighlightType('range');
                            }
                          }}
                        >
                          <div className="w-full h-full flex items-end">
                            <div className="w-full relative">
                              {/* Número encima de la barra */}
                              {count > 0 && (
                                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 text-[10px] text-gray-600 z-10">
                                  {count}
                                </div>
                              )}
                              <div
                                className={`w-full rounded-t transition-all ${
                                  isWinnerInBucket ? 'bg-yellow-500' : isActive ? 'bg-green-600' : 'bg-gray-400'
                                }`}
                                style={{ height: `${height * 0.4}px` }}
                              />
                            </div>
                          </div>
                          {/* Tooltip */}
                          <div className="opacity-0 group-hover:opacity-100 absolute bottom-full mb-6 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap transition-opacity pointer-events-none z-10">
                            {count} {count === 1 ? t('buybox.one_seller', 'vendedor') : t('buybox.multiple_sellers', 'vendedores')}
                            <div className="text-yellow-200 text-xs">
                              {formatPrice(priceStats.min + index * priceStats.bucketSize)} - 
                              {formatPrice(priceStats.min + (index + 1) * priceStats.bucketSize)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            
            {/* Tabla de variantes */}
            <div className="max-w-4xl">
              {/* Indicador de scroll si hay más de 20 variantes */}
              {sortedVariants.length > 20 && (
                <div className="text-xs text-gray-500 mb-2 text-right">
                  {t('common.total', 'Total')}: {sortedVariants.length} {t('buybox.multiple_sellers', 'vendedores')} - {t('common.scroll_to_see_all', 'Desplaza para ver todos')}
                </div>
              )}
              <div className="relative border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-y-auto" style={{ maxHeight: '440px' }}>
                  <table className="w-full">
                    <thead className="bg-gray-100 text-xs text-gray-700 sticky top-0 z-10">
                      <tr>
                        <th className="px-2 py-1 text-center font-medium w-8">#</th>
                        <th className="px-2 py-1 text-left font-medium min-w-[200px]">{t('buybox.vendor_column', 'Vendedor')}</th>
                        <th className="px-2 py-1 text-right font-medium whitespace-nowrap">{t('buybox.original_price_column', 'P. Original')}</th>
                        <th className="px-2 py-1 text-right font-medium whitespace-nowrap">{t('buybox.discounted_price_column', 'P. Desc.')}</th>
                        <th className="px-2 py-1 text-right font-medium whitespace-nowrap">{t('buybox.sale_price_column', 'P. Venta')}</th>
                        <th className="px-2 py-1 text-center font-medium">{t('buybox.installments_column', 'Cuotas')}</th>
                        <th className="px-2 py-1 text-right font-medium whitespace-nowrap">{t('buybox.installment_price_column', 'P. Cuota')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                  {sortedVariants.map((variant, index) => {
                    const isWinner = variant.buybox_winner === 1 || variant.buybox_winner === '1' || variant.buybox_winner === true;
                    const sellerValue = variant.Seller || variant.seller || t('buybox.unknown_seller', 'Vendedor desconocido');
                    const uniqueKey = variant.item_id ? `item-${variant.item_id}` : `${variant.id_catalogo}-${sellerValue}-${index}`;
                    const variantPrice = typeof variant.sale_price === 'number' ? variant.sale_price : parseFloat(variant.sale_price);
                    const isHighlighted = highlightedPrices.has(variantPrice);
                    
                    return (
                      <tr 
                        key={uniqueKey}
                        className={`
                          ${isHighlighted ? 'bg-green-100' : isWinner ? 'bg-yellow-50 font-medium' : 'bg-white'}
                          hover:bg-gray-50 transition-colors
                        `}
                      >
                        <td className="px-2 py-2 text-xs text-center text-gray-500 font-medium">
                          {index + 1}
                        </td>
                        <td className="px-2 py-2 text-xs">
                          <div className="flex items-center">
                            {isWinner && (
                              <Trophy className="h-3 w-3 text-yellow-600 mr-1 flex-shrink-0" />
                            )}
                            <span className={`${isWinner ? 'text-gray-900' : 'text-gray-700'}`} title={sellerValue}>
                              {sellerValue}
                            </span>
                          </div>
                        </td>
                        <td className="px-2 py-2 text-xs text-right text-gray-700 whitespace-nowrap">
                          {variant.original_price ? formatPrice(variant.original_price) : '-'}
                        </td>
                        <td className="px-2 py-2 text-xs text-right text-gray-700 whitespace-nowrap">
                          {variant.discounted_price ? formatPrice(variant.discounted_price) : '-'}
                        </td>
                        <td className={`px-2 py-2 text-xs text-right whitespace-nowrap font-bold ${isWinner ? 'text-yellow-700' : 'text-gray-900'}`}>
                          {formatPrice(variant.sale_price)}
                        </td>
                        <td className="px-2 py-2 text-xs text-center text-gray-700">
                          {variant.installements || variant.installments || variant.cuotas || '-'}
                        </td>
                        <td className="px-2 py-2 text-xs text-right text-gray-700 whitespace-nowrap">
                          {(() => {
                            // Si ya tiene precio de cuota, usarlo
                            const existingValue = variant.installements_value || variant.installments_value || variant.precio_cuota;
                            if (existingValue) {
                              return formatPrice(existingValue);
                            }
                            
                            // Si no tiene precio de cuota pero sí tiene cuotas, calcular
                            const cuotas = variant.installements || variant.installments || variant.cuotas;
                            if (cuotas && variant.sale_price) {
                              const numCuotas = typeof cuotas === 'string' ? parseInt(cuotas) : cuotas;
                              const salePrice = typeof variant.sale_price === 'string' ? parseFloat(variant.sale_price) : variant.sale_price;
                              if (numCuotas > 0 && salePrice > 0) {
                                return formatPrice(salePrice / numCuotas);
                              }
                            }
                            
                            return '-';
                          })()}
                        </td>
                      </tr>
                    );
                  })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </td>
      </tr>
    </>
  );
};

export default BuyBoxVariants;